import { Heading, Text } from 'components'
import type { AutopilotPublishMode } from '../../types'
import type { ConnectableChannel } from '../../utils/weekly-queue-utils'

export interface MissingPlatform {
  channel: ConnectableChannel
  postCount: number
  soonestScheduledAt?: string | null
}

interface WeekStatusHeroProps {
  /**
   * Posts awaiting the user's approval (review-mode plans only). This is the
   * actionable bucket — the headline leads with it whenever it's non-zero.
   */
  toReviewCount: number
  /**
   * Posts locked onto the publish rails but not yet live: approved autopilot
   * posts, automatic-mode posts, and anything the user scheduled by hand from
   * chat or ideas. No approval gate stands between these and publishing.
   */
  scheduledCount: number
  /** Published (live) in the visible week. */
  decidedCount: number
  hasMissingPlatforms: boolean
  /** Plan is paused — overrides the copy and suppresses the live dot. */
  isPaused?: boolean
  /**
   * Active plan's publish mode. In 'review' nothing publishes unattended, so
   * the copy must say "review/approve" rather than "publish automatically".
   * Undefined when there's no active plan (e.g. only hand-scheduled posts).
   */
  publishMode?: AutopilotPublishMode
}

export function WeekStatusHero({
  toReviewCount,
  scheduledCount,
  decidedCount,
  hasMissingPlatforms,
  isPaused,
  publishMode,
}: WeekStatusHeroProps) {
  const hasActivity = toReviewCount + scheduledCount > 0 && !isPaused
  const { headline, sub } = isPaused
    ? pausedCopy(toReviewCount + scheduledCount + decidedCount)
    : heroCopy(toReviewCount, scheduledCount, decidedCount, hasMissingPlatforms, publishMode)
  return (
    <div className="flex min-w-0 flex-col gap-2">
      <Heading level="h2" className="flex min-w-0 items-center gap-3">
        {hasActivity && <span className="liveDot" aria-hidden />}
        {headline}
      </Heading>
      {sub && (
        <Text variant="body-sm" as="p">
          {sub}
        </Text>
      )}
    </div>
  )
}

// Paused overrides everything — make it unmistakable that nothing will publish.
function pausedCopy(total: number): { headline: string; sub?: string } {
  return {
    headline: 'Autopilot paused. Posts are on hold.',
    sub:
      total > 0
        ? 'Scheduled posts won’t publish until you resume.'
        : 'Resume your plan to start drafting and publishing again.',
  }
}

// The headline is a function of state, not a static total. It splits the week
// into two buckets — posts that still need the user ("to review") and posts
// already locked in ("scheduled") — and leads with whichever is actionable,
// degrading to a settled state once nothing is left to approve. That single
// rule covers every combination of plan mode, approval progress, and post
// source (autopilot vs hand-scheduled), so the copy responds the moment a
// user approves a post instead of freezing on the original count.
function heroCopy(
  toReviewCount: number,
  scheduledCount: number,
  decidedCount: number,
  hasMissingPlatforms: boolean,
  publishMode?: AutopilotPublishMode
): { headline: string; sub?: string } {
  const postsLabel = (n: number) => (n === 1 ? '1 post' : `${n} posts`)
  const scheduledPostsLabel = (n: number) => (n === 1 ? '1 scheduled post' : `${n} scheduled posts`)

  // 1. Action needed. Review-mode posts await approval. Lead with the *pending*
  //    count so the headline ticks down as the user approves each one. Only
  //    'review' plans ever produce these, so the verb is always "approve".
  if (toReviewCount > 0) {
    const needs = toReviewCount === 1 ? 'needs' : 'need'
    return {
      headline: `${postsLabel(toReviewCount)} ${needs} your approval.`,
      sub:
        scheduledCount > 0
          ? `Approve each one to publish · ${scheduledCount} already scheduled.`
          : 'Approve each one to publish.',
    }
  }

  // 2. Nothing to review — settled states. Everything left is on the rails.
  if (scheduledCount > 0) {
    if (decidedCount > 0) {
      return {
        headline:
          scheduledCount === 1
            ? '1 scheduled post coming up.'
            : `${scheduledCount} scheduled posts coming up.`,
        sub: `${decidedCount} already live.`,
      }
    }
    // Automatic plans publish hands-off; review plans (now fully approved) and
    // hand-scheduled posts are simply set. Never promise auto-publish unless the
    // plan actually runs that way.
    if (publishMode === 'automatic') {
      return {
        headline: `${postsLabel(scheduledCount)} scheduled this week.`,
        sub: 'Publishing automatically. Edit any before it goes live.',
      }
    }
    return {
      headline: 'Your week is set.',
      sub: `${postsLabel(scheduledCount)} scheduled to publish.`,
    }
  }

  if (hasMissingPlatforms) {
    return {
      headline: 'Connect your accounts to start publishing.',
      sub: 'Drafts are ready. We just need account access.',
    }
  }
  if (decidedCount === 0) {
    return {
      headline: 'Your calendar is empty this week.',
      sub: 'Add a post or tweak your plan.',
    }
  }
  return {
    headline: `Your week is live. ${scheduledPostsLabel(decidedCount)} published.`,
    sub: 'Next batch lands Sunday.',
  }
}
